/**
 * <s-envelope> — sealed invitation envelope on the password page.
 *
 * Tapping the envelope ([data-envelope-open]) sets [data-open] on the host; CSS
 * breaks the seal, swings the flap open and slides the invitation card out.
 * Once opened, the envelope is hidden and focus moves to [data-envelope-focus].
 * The opened state is remembered for the browsing session ([data-instant] skips
 * the animation), so a visitor returning after signing up sees the card at once.
 * In the editor ([data-always-sealed]) the envelope always starts sealed.
 * Reduced motion is handled in CSS (no transitions).
 */
const STORAGE_KEY = 'manifesto-invite-open';
const REVEAL_MS = 1700;

class SEnvelope extends HTMLElement {
  #button = null;
  #timer = 0;
  #onClick = () => this.#open();

  connectedCallback() {
    this.#button = this.querySelector('[data-envelope-open]');
    let remembered = false;
    try {
      remembered = window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      remembered = false;
    }
    if (remembered && !this.hasAttribute('data-always-sealed')) {
      this.setAttribute('data-instant', '');
      this.setAttribute('data-open', '');
      if (this.#button) this.#button.hidden = true;
      return;
    }
    this.#button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    window.clearTimeout(this.#timer);
  }

  #open() {
    if (this.hasAttribute('data-open')) return;
    this.setAttribute('data-open', '');
    try {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* storage unavailable: the envelope simply shows again next visit */
    }
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#timer = window.setTimeout(() => {
      if (this.#button) this.#button.hidden = true;
      this.querySelector('[data-envelope-focus]')?.focus({ preventScroll: true });
    }, calm ? 0 : REVEAL_MS);
  }
}

if (!customElements.get('s-envelope')) customElements.define('s-envelope', SEnvelope);
